import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { isMembershipCurrent, parseMembership, redirectToBilling, type Membership } from "../utils/billing";
import { clearAuthState, setAuthState } from "./authSlice";

type SessionState = { auth: { user: { sub?: string } | null } };
export type BillingState = {
  ownerId: string | null; membership: Membership | null; loading: boolean; requestId: string | null;
  checkoutLoading: boolean; portalLoading: boolean; error: string | null;
  checkoutRequestId: string | null; portalRequestId: string | null;
};
const initialState: BillingState = { ownerId: null, membership: null, loading: false, requestId: null,
  checkoutLoading: false, portalLoading: false, error: null, checkoutRequestId: null, portalRequestId: null };

export const loadMembership = createAsyncThunk<Membership, { force?: boolean } | undefined>(
  "billing/load", async (options, { getState, signal }) => {
    const ownerId = (getState() as SessionState).auth.user?.sub;
    const value = await request(`${API_URLS.billingEntitlements}${options?.force ? "?refresh=1" : ""}`, { auth: true });
    if (signal.aborted || ownerId !== (getState() as SessionState).auth.user?.sub) throw new Error("Your session changed. Please retry.");
    return parseMembership(value);
  },
);

export const startCheckout = createAsyncThunk<void, "monthly" | "annual">(
  "billing/checkout", async (billingInterval, { getState, signal }) => {
    const ownerId = (getState() as SessionState).auth.user?.sub;
    if (!ownerId) throw new Error("Please sign in before subscribing.");
    const result = await request(API_URLS.createCheckoutSession, { method: "POST", auth: true, body: { billingInterval } });
    if (signal.aborted || ownerId !== (getState() as SessionState).auth.user?.sub) throw new Error("Your session changed. Please retry.");
    redirectToBilling(result?.url);
  },
);

export const openBillingPortal = createAsyncThunk<void, string | undefined>(
  "billing/portal", async (subscriptionId, { getState, signal }) => {
    const ownerId = (getState() as SessionState).auth.user?.sub;
    if (!ownerId) throw new Error("Please sign in to manage billing.");
    const result = await request(API_URLS.stripePortal, { method: "POST", auth: true, body: subscriptionId ? { subscriptionId } : {} });
    if (signal.aborted || ownerId !== (getState() as SessionState).auth.user?.sub) throw new Error("Your session changed. Please retry.");
    redirectToBilling(result?.url);
  },
);

const billingSlice = createSlice({
  name: "billing", initialState,
  reducers: {
    expireMembership: (state) => { if (state.membership && !isMembershipCurrent(state.membership)) state.membership = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearAuthState, () => initialState)
      .addCase(setAuthState, (state, action) => {
        const ownerId = action.payload?.user?.sub ?? null;
        if (ownerId !== state.ownerId) return { ...initialState, ownerId };
      })
      .addCase(loadMembership.pending, (state, action) => { state.loading = true; state.requestId = action.meta.requestId; state.error = null; })
      .addCase(loadMembership.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.membership = action.payload; state.loading = false; state.requestId = null;
      })
      .addCase(loadMembership.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        if (!isMembershipCurrent(state.membership)) state.membership = null;
        state.loading = false; state.requestId = null; state.error = action.error.message || "Unable to verify membership.";
      })
      .addCase(startCheckout.pending, (state, action) => { state.checkoutLoading = true; state.error = null; state.checkoutRequestId = action.meta.requestId; })
      .addCase(startCheckout.fulfilled, (state, action) => {
        if (state.checkoutRequestId !== action.meta.requestId) return;
        state.checkoutLoading = false; state.checkoutRequestId = null;
      })
      .addCase(startCheckout.rejected, (state, action) => {
        if (state.checkoutRequestId !== action.meta.requestId) return;
        state.checkoutLoading = false; state.checkoutRequestId = null; state.error = action.error.message || "Checkout could not be started.";
      })
      .addCase(openBillingPortal.pending, (state, action) => { state.portalLoading = true; state.error = null; state.portalRequestId = action.meta.requestId; })
      .addCase(openBillingPortal.fulfilled, (state, action) => {
        if (state.portalRequestId !== action.meta.requestId) return;
        state.portalLoading = false; state.portalRequestId = null;
      })
      .addCase(openBillingPortal.rejected, (state, action) => {
        if (state.portalRequestId !== action.meta.requestId) return;
        state.portalLoading = false; state.portalRequestId = null; state.error = action.error.message || "Billing management could not be opened.";
      });
  },
});

export const { expireMembership } = billingSlice.actions;
export const selectIsPremium = (state: { billing: BillingState }) => isMembershipCurrent(state.billing.membership) && state.billing.membership?.premium === true;
export default billingSlice.reducer;
